import { Card } from "@/components/ui/card";
import { Check, Clock, Plus } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { useState } from "react";
import CreatePaymentForm from "@/components/CreatePaymentForm";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Tables } from "@/integrations/supabase/types";
import { ScrollArea } from "@/components/ui/scroll-area";
import { format, parseISO } from "date-fns";
import { es } from "date-fns/locale";

type Payment = Tables<"payments">;

const fetchPayments = async () => {
  const { data, error } = await supabase
    .from("payments")
    .select("*")
    .order("payment_date", { ascending: false });

  if (error) throw error;
  return data;
};

const PaymentCard = ({ payment }: { payment: Payment }) => {
  // Ensure correct date by handling timezone
  const paymentDate = new Date(payment.payment_date + 'T00:00:00');
  
  return (
    <Link key={payment.id} to={`/payments/${payment.id}`}>
      <Card className="p-4 hover:shadow-md transition-shadow">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-semibold">{payment.description || "Pago"}</h3>
            <p className="text-sm text-gray-600">
              {format(parseISO(payment.payment_date), "d 'de' MMMM 'de' yyyy", { locale: es })}
            </p>
          </div>
          <div className="flex items-center space-x-4">
            <p className="font-semibold">${Math.round(Number(payment.amount)).toLocaleString()}</p>
            <div className={`p-2 rounded-full ${
              payment.status === 'paid' 
                ? 'bg-secondary text-secondary-foreground' 
                : 'bg-accent text-accent-foreground'
            }`}>
              {payment.status === 'paid' ? <Check className="w-4 h-4" /> : <Clock className="w-4 h-4" />}
            </div>
          </div>
        </div>
      </Card>
    </Link>
  );
};

const Payments = () => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [activeView, setActiveView] = useState("pending");
  
  const { data: payments = [], isLoading, error } = useQuery({
    queryKey: ['payments'],
    queryFn: fetchPayments,
  });

  // Filter payments based on active view
  const filteredPayments = payments.filter(p => 
    activeView === "pending" ? p.status === "pending" : p.status === "paid"
  );

  // Group payments by month
  const groupedPayments = filteredPayments.reduce((groups, payment) => {
    const date = parseISO(payment.payment_date);
    const monthYear = format(date, 'MMMM yyyy', { locale: es });
    
    if (!groups[monthYear]) {
      groups[monthYear] = [];
    }
    groups[monthYear].push(payment);
    return groups;
  }, {} as Record<string, Payment[]>);

  if (isLoading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center text-red-500">
        Error al cargar los pagos. Por favor intente más tarde.
      </div>
    );
  }

  return (
    <div className="p-4 max-w-[1600px] mx-auto h-[calc(100vh-2rem)] flex flex-col">
      <div className="space-y-6 flex-none">
        <div className="flex md:flex-row flex-col md:items-center gap-4">
          <div className="flex-1">
            <h1 className="text-3xl font-bold text-gray-800">Estado de pagos</h1>
            <p className="text-muted-foreground mt-1">Monitorea el estado e historial de tus pagos</p>
          </div>
          <div className="md:w-auto w-full">
            <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
              <DialogTrigger asChild>
                <Button className="w-full h-12">
                  <Plus className="w-4 h-4 mr-2" />
                  Crear Pago
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Crear Nuevo Pago</DialogTitle>
                </DialogHeader>
                <CreatePaymentForm onSuccess={() => setIsDialogOpen(false)} />
              </DialogContent>
            </Dialog>
          </div>
        </div>
        
        <div className="flex justify-center mb-6">
          <ToggleGroup 
            type="single" 
            value={activeView} 
            onValueChange={(value) => value && setActiveView(value)}
            className="bg-[#F1F1F1] p-1 rounded-lg w-full md:w-auto"
          >
            <ToggleGroupItem 
              value="pending" 
              aria-label="Mostrar pagos pendientes"
              className="flex-1 md:flex-initial px-4 md:px-8 data-[state=on]:bg-primary data-[state=on]:text-primary-foreground"
            >
              <div className="flex items-center justify-center gap-2">
                <Clock className="w-4 h-4" />
                Pendiente
              </div>
            </ToggleGroupItem>
            <ToggleGroupItem 
              value="completed" 
              aria-label="Mostrar pagos completados"
              className="flex-1 md:flex-initial px-4 md:px-8 data-[state=on]:bg-primary data-[state=on]:text-primary-foreground"
            >
              <div className="flex items-center justify-center gap-2">
                <Check className="w-4 h-4" />
                Completado
              </div>
            </ToggleGroupItem>
          </ToggleGroup>
        </div>
      </div>

      <ScrollArea className="flex-1 -mx-4 px-4">
        <div className="space-y-8">
          {Object.entries(groupedPayments)
            .sort((a, b) => {
              const dateA = parseISO(a[0].replace(/(\w+) (\d{4})/, '$2-$1-01'));
              const dateB = parseISO(b[0].replace(/(\w+) (\d{4})/, '$2-$1-01'));
              return dateB.getTime() - dateA.getTime();
            })
            .map(([monthYear, monthPayments]) => (
            <div key={monthYear} className="space-y-4">
              <h3 className="text-lg font-semibold">{monthYear.charAt(0).toUpperCase() + monthYear.slice(1)}</h3>
              <div className="grid gap-4">
                {monthPayments.map(payment => (
                  <PaymentCard key={payment.id} payment={payment} />
                ))}
              </div>
            </div>
          ))}
          {Object.keys(groupedPayments).length === 0 && (
            <p className="text-center text-muted-foreground">No se encontraron pagos</p>
          )}
        </div>
      </ScrollArea>
    </div>
  );
};

export default Payments;